import { getChartLabels } from "lib/utils";
import ReactECharts from "echarts-for-react";
import { getUSANumberFormat } from "app/utils/utils";
import { intToString } from "app/uicomponents/macrocomponents/PolicySummary/config";

const OutsideCollateralRequirement = ({
  isDarkMode,
  selectedSeriesRef,
  zoomStateRef,
  color,
  graphData,
  selectedChartFilter,
}: {
  isDarkMode: boolean;
  selectedSeriesRef: any;
  zoomStateRef: any;
  color: string;
  graphData: any;
  selectedChartFilter: string;
}) => {
  const loan = graphData?.map((p: any) => p.outsideCollateralRequirement.loan);
  const stressedCashValue = graphData?.map(
    (p: any) => p.outsideCollateralRequirement.stressedCashValue,
  );

  const option = {
    title: {
      text: "Difference between EOY Outstanding Financing Balance and Stressed Surrender Value",
      left: "left",
      top: "top",
      textStyle: {
        fontSize: 16,
        color: isDarkMode ? "#ffffff" : "#2A2A3D",
      },
    },
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "none",
      },
      formatter: (params: any) =>
        params
          .filter(
            (item: any) =>
              item.seriesName === "EOY Outstanding Financing Balance" ||
              item.seriesName === "Stressed Surrender value",
          ) // exclude Difference
          .map((item: any) => {
            const value = Math.round(item.value);
            return `${item.seriesName}: $${getUSANumberFormat(value, false)}`;
          })
          .join("<br/>"),
    },
    legend: {
      data: ["EOY Outstanding Financing Balance", "Stressed Surrender value"],
      right: "5%",
      top: "top",
      selectedMode: false,
      textStyle: {
        color: isDarkMode ? "#ffffff" : "#2A2A3D",
      },
    },
    xAxis: {
      type: "category",
      data: getChartLabels(graphData, selectedChartFilter),
      axisLabel: {
        color: `${color}`,
        fontFamily: "Conthrax Semi-Bold",
        fontSize: 10,
      },
    },
    yAxis: {
      type: "value",
      name: "Amount",
      nameLocation: "middle",
      nameGap: 60,
      nameTextStyle: {
        color,
        fontSize: 12,
      },
      splitLine: {
        show: false, // Hide horizontal grid lines
      },
      min: 0,
      axisLabel: {
        color: `${color}`,
        formatter: (value: number) => `$${intToString(value)}`,
        fontFamily: "Conthrax Semi-Bold",
        fontSize: 10,
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: `${color}`,
        },
      },
    },
    dataZoom: [
      {
        type: "slider",
        show: true,
        start: zoomStateRef.current?.start || 0,
        end: zoomStateRef.current?.end || Math.min(100, (10 / (graphData?.length || 1)) * 100),
        minValueSpan: 1,
        maxValueSpan: graphData?.length || 1,
        height: 20,
        bottom: 10,
        borderColor: "transparent",
        textStyle: {
          color: `${color}`,
          fontSize: 10,
        },
        showDetail: true,
        showDataShadow: true,
        realtime: true,
        rangeMode: ["percent", "percent"],
        filterMode: "filter",
        xAxisIndex: 0,
      },
    ],
    series: [
      {
        name: "EOY Outstanding Financing Balance",
        type: "line",
        data: loan,
        showSymbol: false,
        label: { show: false },
        lineStyle: { color: "grey" },
        itemStyle: { color: "grey" },
      },
      {
        name: "Stressed Surrender value",
        type: "line",
        showSymbol: false,
        data: stressedCashValue,
        label: { show: false },
        lineStyle: { color: "#00478199" },
        itemStyle: { color: "#00478199" },
      },
      {
        name: "Difference",
        type: "custom",
        renderItem: (params: any, api: any) => {
          const xIndex = api.value(0);
          const y1 = api.value(1); // Loan
          const y2 = api.value(2); // Stressed Cash Value

          const x = api.coord([xIndex, y1])[0];
          const yStart = api.coord([xIndex, y1])[1];
          const yEnd = api.coord([xIndex, y2])[1];

          // Midpoint
          const yMid = (yStart + yEnd) / 2;
          const diff = y1 - y2; // Loan -  Stressed Cash Value
          return {
            type: "group",
            children: [
              // Vertical line
              {
                type: "line",
                shape: { x1: x, y1: yStart, x2: x, y2: yEnd },
                style: { stroke: "oklch(70.7% 0.165 254.624)", lineWidth: 1.5 },
              },
              // Difference text with background color
              {
                type: "text",
                style: {
                  text: getUSANumberFormat(diff, false),
                  x,
                  y: yMid,
                  fill: isDarkMode ? "#ffffff" : "#2A2A3D",
                  fontSize: 14,
                  textAlign: "center",
                  textVerticalAlign: "middle",
                  backgroundColor: isDarkMode ? "#2A2A3D" : "#ffffff",
                  padding: [2, 4],
                  borderRadius: 3,
                },
              },
            ],
          };
        },
        encode: { x: 0, y: [1, 2] },
        data: loan.map((a: any, i: string | number) => [i, a, stressedCashValue[i]]),
      },
    ],
    grid: {
      top: 50,
      left: 60,
      right: 60,
      bottom: 70,
    },
  };

  return (
    <div style={{ width: "100%", height: "365px", margin: 0, padding: 0 }} className="w-full">
      <ReactECharts
        onEvents={{
          click: (params: any) => {
            if (params.seriesName) {
              selectedSeriesRef.current = params.seriesName;
            }
          },
          mouseover: (params: any) => {
            if (params.seriesName) {
              selectedSeriesRef.current = params.seriesName;
            }
          },
          mouseout: () => {
            selectedSeriesRef.current = null;
          },
          dataZoom: (params: any) => {
            zoomStateRef.current = { start: params.start, end: params.end };
          },
        }}
        option={option}
        style={{ height: "100%", width: "100%" }}
        opts={{ renderer: "canvas" }}
      />
    </div>
  );
};

export default OutsideCollateralRequirement;
